<template>
	<el-upload
		:action="'#'"
		:http-request="onFileUpload"
		:accept="fileTypes == null ? '' : fileTypes.join(',')"
		:show-file-list="false"
		:disabled="disabled"
		:before-upload="beforeUpload"
		:multiple="true"
	>
		<slot></slot>
	</el-upload>
</template>

<script>
export default {
	name: "fileUpload",

	data() {
		return {
			loading: null,
			uploadHeaders: {
				"accessToken": sessionStorage.getItem("accessToken")
			}
		};
	},

	props: {
		action: {
			type: String,
			required: false
		},
		fileTypes: {
			type: Array,
			default: null
		},
		maxSize: {
			type: Number,
			default: null
		},
		showLoading: {
			type: Boolean,
			default: false
		},
		isPermanent: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},

	methods: {
		async onFileUpload(file) {
			// 展示加载条
			if (this.showLoading) {
				this.loading = this.$loading({
					lock: true,
					text: "正在上传...",
					spinner: "el-icon-loading",
					background: "rgba(0, 0, 0, 0.7)"
				});
			}

			// 默认使用原文件
			let uploadFile = file.file;

			try {
				// 只处理图片，视频和普通文件保持原逻辑
				if (
					uploadFile &&
					uploadFile.type &&
					uploadFile.type.startsWith("image/")
				) {
					uploadFile = await this.compressImage(uploadFile);
				}
			} catch (error) {
				// 压缩失败不影响上传，继续使用原图
				console.warn("图片压缩失败，使用原图上传：", error);
				uploadFile = file.file;
			}

			let formData = new FormData();
			formData.append("file", uploadFile);

			let url = this.action;
			url += this.action.includes("?") ? "&" : "?";
			url += "isPermanent=" + this.isPermanent;

			this.$http({
				url: url,
				data: formData,
				method: "post",
				headers: {
					"Content-Type": "multipart/form-data"
				}
			})
				.then((data) => {
					this.$emit("success", data, uploadFile);
				})
				.catch((e) => {
					this.$emit("fail", e, uploadFile);
				})
				.finally(() => {
					this.loading && this.loading.close();
				});
		},

		/**
		 * 压缩图片
		 */
		compressImage(file) {
			return new Promise((resolve) => {
				const originalSize = file.size || 0;

				// console.log(
				// 	"[图片压缩] 压缩前：",
				// 	this.formatFileSize(originalSize),
				// 	file.name,
				// 	file.type
				// );

				// GIF 使用 Canvas 会丢失动画，所以不压缩
				if (
					file.type === "image/gif" ||
					/\.gif$/i.test(file.name || "")
				) {
					// console.log("[图片压缩] GIF 图片跳过压缩");
					resolve(file);
					return;
				}

				const objectUrl = URL.createObjectURL(file);
				const image = new Image();

				image.onload = () => {
					try {
						const sourceWidth =
							image.naturalWidth || image.width;

						const sourceHeight =
							image.naturalHeight || image.height;

						if (!sourceWidth || !sourceHeight) {
							URL.revokeObjectURL(objectUrl);
							console.warn(
								"[图片压缩] 无法获取图片尺寸，使用原图"
							);
							resolve(file);
							return;
						}

						// 最长边限制为 1280px
						const maxSize = 1280;

						const scale = Math.min(
							maxSize / sourceWidth,
							maxSize / sourceHeight,
							1
						);

						const targetWidth = Math.max(
							1,
							Math.round(sourceWidth * scale)
						);

						const targetHeight = Math.max(
							1,
							Math.round(sourceHeight * scale)
						);

						const canvas =
							document.createElement("canvas");

						canvas.width = targetWidth;
						canvas.height = targetHeight;

						const context = canvas.getContext("2d");

						if (!context) {
							URL.revokeObjectURL(objectUrl);
							console.warn(
								"[图片压缩] 浏览器不支持 Canvas，使用原图"
							);
							resolve(file);
							return;
						}

						/*
						 * 输出 JPEG。
						 * PNG 透明区域先填充白色，防止转换后背景变黑。
						 */
						context.fillStyle = "#ffffff";
						context.fillRect(
							0,
							0,
							targetWidth,
							targetHeight
						);

						context.drawImage(
							image,
							0,
							0,
							targetWidth,
							targetHeight
						);

						canvas.toBlob(
							(blob) => {
								URL.revokeObjectURL(objectUrl);

								if (!blob) {
									console.warn(
										"[图片压缩] 生成压缩图片失败，使用原图"
									);
									resolve(file);
									return;
								}

								const compressedFile = new File(
									[blob],
									this.getCompressedFileName(
										file.name
									),
									{
										type: "image/jpeg",
										lastModified:
											file.lastModified ||
											Date.now()
									}
								);

								/*
								 * Element Upload 和聊天组件会在原 File 对象上
								 * 保存 uid、msgInfo、chat 等数据。
								 * 创建新 File 后必须复制，否则成功回调可能取不到。
								 */
								this.copyFileProperties(
									file,
									compressedFile
								);

								// console.log(
								// 	"[图片压缩] 原始尺寸：",
								// 	`${sourceWidth} × ${sourceHeight}`
								// );

								// console.log(
								// 	"[图片压缩] 压缩尺寸：",
								// 	`${targetWidth} × ${targetHeight}`
								// );

								// console.log(
								// 	"[图片压缩] 压缩后：",
								// 	this.formatFileSize(
								// 		compressedFile.size
								// 	),
								// 	compressedFile.name,
								// 	compressedFile.type
								// );

								/*
								 * 已经高度压缩的图片，重新编码后可能反而更大。
								 * 这种情况继续上传原图。
								 */
								if (
									originalSize > 0 &&
									compressedFile.size >=
										originalSize
								) {
									// console.log(
									// 	"[图片压缩] 压缩后没有变小，使用原图上传"
									// );

									resolve(file);
									return;
								}

								const savedSize =
									originalSize -
									compressedFile.size;

								const savedPercent =
									originalSize > 0
										? (
												(savedSize /
													originalSize) *
												100
										  ).toFixed(2)
										: 0;

								// console.log(
								// 	`[图片压缩] 已减少 ${this.formatFileSize(
								// 		savedSize
								// 	)}，压缩率 ${savedPercent}%`
								// );

								resolve(compressedFile);
							},
							"image/jpeg",
							0.7
						);
					} catch (error) {
						URL.revokeObjectURL(objectUrl);

						// console.warn(
						// 	"[图片压缩] 处理异常，使用原图：",
						// 	error
						// );

						resolve(file);
					}
				};

				image.onerror = () => {
					URL.revokeObjectURL(objectUrl);

					// console.warn(
					// 	"[图片压缩] 图片加载失败，使用原图"
					// );

					resolve(file);
				};

				image.src = objectUrl;
			});
		},

		/**
		 * 保留原文件上的自定义属性。
		 */
		copyFileProperties(sourceFile, targetFile) {
			const propertyNames = Object.keys(sourceFile);

			propertyNames.forEach((key) => {
				try {
					targetFile[key] = sourceFile[key];
				} catch (error) {
					console.warn(
						`复制文件属性 ${key} 失败：`,
						error
					);
				}
			});

			// 确保聊天业务依赖的属性存在
			if (sourceFile.uid !== undefined) {
				targetFile.uid = sourceFile.uid;
			}

			if (sourceFile.msgInfo !== undefined) {
				targetFile.msgInfo = sourceFile.msgInfo;
			}

			if (sourceFile.chat !== undefined) {
				targetFile.chat = sourceFile.chat;
			}
		},

		/**
		 * 压缩后统一使用 jpg 后缀。
		 */
		getCompressedFileName(fileName) {
			const name = fileName || `image_${Date.now()}`;

			const baseName = name.replace(/\.[^.]+$/, "");

			return `${baseName}_compressed.jpg`;
		},

		formatFileSize(size) {
			const bytes = Number(size) || 0;

			if (bytes < 1024) {
				return `${bytes} B`;
			}

			if (bytes < 1024 * 1024) {
				return `${(bytes / 1024).toFixed(2)} KB`;
			}

			return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
		},

		beforeUpload(file) {
			// 校验文件类型
			if (this.fileTypes && this.fileTypes.length > 0) {
				let fileType = file.type;

				let t = this.fileTypes.find(
					(t) => t.toLowerCase() === fileType
				);

				if (t === undefined) {
					this.$message.error(
						`文件格式错误，请上传以下格式的文件：${this.fileTypes.join(
							"、"
						)}`
					);

					return false;
				}
			}

			// 校验大小
			if (this.maxSize && file.size > this.maxSize) {
				this.$message.error(
					`文件大小不能超过 ${this.fileSizeStr}!`
				);

				return false;
			}

			this.$emit("before", file);

			return true;
		}
	},

	computed: {
		fileSizeStr() {
			if (this.maxSize > 1024 * 1024) {
				return (
					Math.round(
						this.maxSize / 1024 / 1024
					) + "M"
				);
			}

			if (this.maxSize > 1024) {
				return (
					Math.round(this.maxSize / 1024) + "KB"
				);
			}

			return this.maxSize + "B";
		}
	}
};
</script>

<style></style>